/* eslint-disable @typescript-eslint/no-empty-function */
import CollapsibleContentSection from "components/company/CollapsibleContentSection";
import ContentSectionBreadcrumbs from "components/company/sections/ContentSectionBreadcrumbs";
import ContentSectionDescriptionLine from "components/company/sections/ContentSectionDescriptionLine";
import ContentSectionHeading from "components/company/sections/ContentSectionHeading";
import Donut, { DEFAULT_DONUT_COLOR_PALETTE } from "components/company/sections/summary/adornments/Donut";
import LineChart from "components/company/sections/summary/adornments/LineChart";
import StaticContentSection from "components/company/StaticContentSection";
import classNames from "components/ui/classNames";
import formatMoneyValue from "components/utils/formatMoneyValue";
import { compareDesc, getYear, parseISO } from "date-fns";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import useSelfie from "lib/hooks/useSelfie";
import { palette } from "lib/miolo";
import parseDateYmd from "lib/utils/parseDateYmd";

interface IOperatingSegmentsSectionProps {
  operatingSegments: NonNullable<IBrazilianCompanyIssuerTopLevelFragment["operatingSegments"]>;
  operatingSegmentsUrl: NonNullable<IBrazilianCompanyIssuerTopLevelFragment["operatingSegmentsUrl"]>;
  financials: NonNullable<IBrazilianCompanyIssuerTopLevelFragment["financials"]>;
}

export default function OperatingSegmentsSection({
  operatingSegments,
  operatingSegmentsUrl,
  financials,
}: IOperatingSegmentsSectionProps) {
  const selfie = useSelfie();
  if (financials.yearly.length === 0) {
    return null;
  }
  const fyFinancials = financials.yearly.find((node) => {
    if (!selfie?.isPremium) {
      return true;
    }
    return !node.isTrailingTwelveMonths && getYear(parseISO(node.endOfPeriod)) === operatingSegments.fiscalYear;
  });
  if (!fyFinancials) {
    return null;
  }
  const hasRevenue = !financials.yearly.every((node) => {
    const netRevenue =
      node.__typename === "YearlyBrazilianBankFinancialStatementRow"
        ? node.financialIntermediationRevenue
        : node.netRevenue;
    return netRevenue === 0;
  });

  const netRevenue =
    fyFinancials.__typename === "YearlyBrazilianBankFinancialStatementRow"
      ? fyFinancials.financialIntermediationRevenue
      : fyFinancials.netRevenue;
  let ebitda = 0;
  if (fyFinancials.__typename !== "YearlyBrazilianBankFinancialStatementRow") {
    ebitda = fyFinancials.ebitda;
  }
  const revenueOrEbitdaUntilReportedFy = financials.yearly
    .filter((node) => {
      return !node.isTrailingTwelveMonths && getYear(parseISO(node.endOfPeriod)) <= operatingSegments.fiscalYear;
    })
    .map((node) => {
      if (node.__typename === "YearlyBrazilianBankFinancialStatementRow") {
        return {
          date: parseDateYmd(node.endOfPeriod),
          value: node.financialIntermediationRevenue,
        };
      }
      return {
        date: parseDateYmd(node.endOfPeriod),
        value: hasRevenue ? node.netRevenue : node.ebitda,
      };
    })
    .sort((a, b) => {
      return compareDesc(a.date, b.date);
    })
    .slice(0, 10);

  if (!selfie?.isPremium) {
    return (
      <CollapsibleContentSection
        title="Fontes de Receita"
        isExpanded={false}
        isPremiumSection={true}
        onToggleCollapsed={() => {}}
        primaryColor={palette.keppel}
        renderSubtitle={() => <ContentSectionBreadcrumbs segments={["Como a empresa ganha dinheiro"]} />}
        renderContentSummary={() => {
          return (
            <div className="flex items-center justify-center space-x-6 xl:justify-start">
              <div className="space-y-1 text-center">
                <h6 className="text-md font-semibold text-gray-700">{operatingSegments.revenueSources.length}</h6>
                <span className="inline-flex text-sm font-medium tracking-wide text-gray-500">
                  {operatingSegments.revenueSources.length === 1 ? "Fonte" : "Fontes"}
                </span>
              </div>

              <Donut
                label={formatMoneyValue(hasRevenue ? netRevenue : ebitda, "short")}
                ariaLabel="Gráfico demonstrando a distribuição das Fontes de Receita"
                chart={{
                  width: 54,
                  height: 54,
                }}
                nodes={operatingSegments.revenueSources.slice(0, 10).map((node) => {
                  return {
                    key: node.description,
                    value: node.proportion,
                  };
                })}
              />
            </div>
          );
        }}
        renderContent={() => null}
      />
    );
  }

  return (
    <div className="w-full">
      <StaticContentSection primaryColor={palette.keppel} className="gap-y-7 md:gap-3">
        <div className="col-span-12 flex flex-col sm:col-span-7 lg:col-span-6 xl:col-span-5">
          <div>
            <ContentSectionHeading>Fontes de Receita</ContentSectionHeading>
            <ContentSectionDescriptionLine>Como a empresa ganha dinheiro</ContentSectionDescriptionLine>
          </div>

          <div>
            <div className="mt-7">
              <div className="grid gap-y-2 pr-6 font-museo-sans-condensed text-sm font-medium tracking-wider sm:pr-0">
                <div className="grid grid-cols-[0.8fr_0.2fr]">
                  <strong className="text-left font-bold uppercase text-slate-800">Segmento</strong>
                  <strong className="text-right font-bold uppercase text-slate-800">%</strong>
                </div>

                {[...operatingSegments.revenueSources]
                  .slice(0, 10)
                  .sort((a, b) => b.proportion - a.proportion)
                  .map((revenueSource, index) => {
                    return (
                      <div
                        key={revenueSource.description}
                        className={classNames(
                          "relative -mx-2 grid grid-cols-[0.8fr_0.2fr]",
                          "rounded py-1 px-2 text-sm font-medium text-slate-700 odd:bg-slate-100 even:bg-white",
                        )}
                      >
                        <div className="truncate text-left">{revenueSource.description}</div>
                        <div className="text-right">{revenueSource.proportion.toFixed(1).replace(".", ",")}</div>

                        <div
                          className="absolute top-2/4 -right-5 h-3 w-3 -translate-y-2/4 rounded-xl"
                          style={{
                            backgroundColor: DEFAULT_DONUT_COLOR_PALETTE[index],
                          }}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          <div className="mt-auto">
            <div className="mt-4">
              <span className="font-museo-sans-condensed text-sm font-medium tracking-wider text-slate-500">
                Ano de Referência: <strong>{operatingSegments.fiscalYear}</strong>
              </span>

              <span className="cursor-pointer font-museo-sans-condensed text-sm font-medium tracking-widest text-slate-600">
                {" • "}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={operatingSegmentsUrl}
                  className="no-underline hover:underline"
                >
                  Ver mais detalhes
                </a>
              </span>
            </div>
          </div>
        </div>

        <div className="col-span-12 flex items-center justify-center sm:col-span-5 lg:col-span-6 xl:col-span-3">
          <Donut
            label={`R$${formatMoneyValue(hasRevenue ? netRevenue : ebitda, "short")}`}
            ariaLabel="Gráfico demonstrando a distribuição das Fontes de Receita"
            chart={{
              width: 200,
              height: 200,
            }}
            nodes={operatingSegments.revenueSources.slice(0, 10).map((node) => {
              return {
                key: node.description,
                value: node.proportion,
              };
            })}
          />
        </div>

        <div className="col-span-12 flex lg:col-span-12 xl:col-span-4">
          <div className="flex h-full flex-col">
            <div>
              <ContentSectionHeading>
                {hasRevenue ? "Crescimento da Receita" : "Crescimento do EBITDA"}
              </ContentSectionHeading>
              <ContentSectionDescriptionLine>Referente aos últimos 10 anos</ContentSectionDescriptionLine>
            </div>

            <div className="mt-auto flex">
              <LineChart
                chart={{
                  width: 320,
                  height: 170,
                  margin: { top: 16, left: 16, bottom: 24, right: 16 },
                  padding: { top: 4, left: 4, bottom: 4, right: 4 },
                  primaryColor: palette.oceanGreen,
                }}
                nodes={revenueOrEbitdaUntilReportedFy}
              />
            </div>
          </div>
        </div>
      </StaticContentSection>
    </div>
  );
}
